import { safeRedirect, siteMap, type Site } from '@church/domain'

/**
 * Which ForHim app this page is (Phase 12e). The same build serves
 * church., services., id. and admin.; the hostname decides the shell. In
 * development (no host map) everything is one origin and sign-in stays in-app.
 */
export const site = siteMap(window.location.hostname, window.location.protocol)

/** The app this page is, or null in development. */
export const currentSite = (): Site | null => site?.site ?? null

/** Pages that belong to id.forhim.io: signing in and everything about the account itself. */
const ID_PATHS = [
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/accept-invitation',
  '/device',
  '/onboarding',
  '/apps',
  // Spotify sends people back here (the redirect URI registered with it).
  '/spotify',
]
export const isIdPath = (pathname: string) =>
  ID_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))

/** Pages that belong to services.forhim.io (ForServices, Phase 12g). */
const SERVICES_PATHS = ['/services', '/my-schedule', '/stand']
export const isServicesPath = (pathname: string) =>
  SERVICES_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))

/** The app a page belongs to, by name: its site, or (in development) its address. */
export function appName(pathname: string): 'ForServices' | 'ForChurch' {
  const here = currentSite()
  const services = here === null ? isServicesPath(pathname) : here === 'services'
  return services ? 'ForServices' : 'ForChurch'
}

/** The same address on another ForHim app (or here, in development). */
export function urlOn(target: Site, pathAndSearch: string): string {
  return site ? `${site.origins[target]}${pathAndSearch}` : pathAndSearch
}

/** id.forhim.io's sign-in, coming back to `back` (this page, by default) afterwards. */
export function signInUrl(back: string = window.location.href): string {
  return urlOn('id', `/sign-in?redirect=${encodeURIComponent(back)}`)
}

/**
 * Where to go after signing in: a ForHim address from `?redirect=`, else
 * `fallback`. id.'s own apps page counts as nowhere in particular (it is where
 * a visit to id.forhim.io starts), so it gives way to `fallback` — their home.
 */
export function afterSignIn(target: string | undefined, fallback = '/'): string {
  const safe = safeRedirect(target, window.location, fallback)
  return currentSite() === 'id' && safe === '/apps' ? fallback : safe
}

/** Leaves the app for another address: a path in-app, a full load elsewhere. */
export function isExternal(href: string): boolean {
  return /^https?:\/\//.test(href) && !href.startsWith(window.location.origin)
}

/**
 * Records an automatic trip back to `target` and says whether it is bouncing:
 * the second in 30 seconds (the trip right after signing in is not counted).
 * An app that cannot see the session sends people straight back here, and id.
 * would send them back again forever; it stops and says so instead. Per tab
 * (sessionStorage).
 */
export function bouncing(target: string, now = Date.now()): boolean {
  const key = 'forhim.bounces'
  try {
    const recent = (
      JSON.parse(sessionStorage.getItem(key) ?? '[]') as { to: string; at: number }[]
    ).filter((b) => now - b.at < 30_000)
    const tries = recent.filter((b) => b.to === target).length
    sessionStorage.setItem(key, JSON.stringify([...recent, { to: target, at: now }]))
    return tries >= 1
  } catch {
    return false
  }
}

/**
 * Where someone goes with no page in mind (Phase 12g): ForServices for the
 * people who only run Sunday (`me.home`, from the API), else id.'s apps page
 * on id. and the dashboard everywhere else.
 */
export function homeUrl(me: { home: 'church' | 'services' } | null | undefined): string {
  if (me?.home === 'services') return urlOn('services', '/')
  return currentSite() === 'id' ? '/apps' : '/'
}
